<template>
  <div ref="container" role="region" :aria-label="regionLabel">
    <!-- D-236 (A1): the widget root is a landmark, so a screen-reader user can jump
         to it and knows what it is once inside the frame. The label matches the
         iframe's `title` (`classes/widget.js`'s `frameTitle`) on purpose: two
         different names for the same box is worse than one, because AT surfaces the
         frame name from outside and the region name from inside.
         🪤 This comment sits INSIDE the root element deliberately. A comment before it
         is a second root node, which makes the component a FRAGMENT — attribute
         fallthrough stops working and `wrapper.element` in a test is no longer this
         div. Measured 2026-09-08: role/aria-label read back as null with the comment
         one line higher. -->
    <!-- P3-2 (MR-31): under Vue 3 the four version subtrees (v4/v3/v2 players +
         the v1 podcast-list) render a single PlayerStub until Phase 4 wires the
         real v4 subtree (MR-43) and the legacy players. The stub keeps the
         version-aware `data-version` so the resize handshake + Playwright oracle
         can tell the build apart. Data/computed/methods below are unchanged;
         they feed the real players once ported.
         The 260px min-height stands in for the real player's height so the
         frozen resize contract (contract.spec facets 3/4/5: the widget re-fits
         to height>224) holds on the Vite build — mirroring the app.stub.vue
         box. It vanishes when Phase 4 replaces this stub with the real player. -->
    <v4-player
      v-if="isReady && version === 4 && contentReady && podcasts.length"
      :podcasts="podcasts"
      :configs="v4PlayerConfigs"
      :api-resource-url="v2Player.apiResourceUrl"
      :api-prefix="apiPrefix"
      :categories="categories"
      @switch-category="switchCategory"
      @image-loaded="imageLoaded"
    ></v4-player>
    <!-- LP-1 (MR-74): the real v1 podcast-list, replacing the PlayerStub for
         version===1. Structure/props mirror the Webpack-baseline app.vue v1
         branch exactly (`#widget-container` carries the dark-theme class; the
         "more categories" rail renders unless `mods=nocategorieslist`), so the
         v1-smoke / v1-audio oracle passes byte-identically on both builds. -->
    <div
      v-else-if="isReady && version === 1"
      id="widget-container"
      :class="{ 'dark-theme': isDarkTheme }"
    >
      <podcast-list
        :category="currentCategory"
        :display-player-inline="displayPlayerInline"
        :drop-to-live="dropToLive"
        :container-width="containerWidth"
        :list="podcasts"
        :page-list="pageList"
        :smc="smc"
        :use-m4a="true"
        :api-prefix="apiPrefix"
      ></podcast-list>
      <categories
        v-if="mods && mods.indexOf('nocategorieslist') < 0"
        :categories="categories"
        :container-width="containerWidth"
      ></categories>
    </div>
    <!-- LP-3 (MR-77): the real v3 gallery player, replacing the PlayerStub for
         version===3. Structure/props mirror the Webpack-baseline app.vue v3
         branch exactly (gated on `podcasts.length` like v4 — the loadData
         empty-playlist placeholder keeps this non-empty, so the v3-smoke /
         v3-audio / v3-share / loadData-edge-v3 oracle assertions pass on both
         builds — render-output parity, not a byte-level guarantee). With no
         data it falls through to the shared stub. -->
    <v3-player
      v-else-if="isReady && version === 3 && podcasts.length"
      :podcasts="podcasts"
      :configs="v3PlayerConfigs"
      :api-resource-url="v2Player.apiResourceUrl"
      :api-prefix="apiPrefix"
      :categories="categories"
      @switch-category="switchCategory"
      @image-loaded="imageLoaded"
    ></v3-player>
    <!-- LP-2 (MR-76): the real v2 live now-playing player, replacing the
         PlayerStub for version===2. Structure/props mirror the Webpack-baseline
         app.vue v2 branch exactly (`version === 2 && isReady` — NO podcasts.length
         gate: v2 fetches its OWN show data via V2Player's apiResourceUrl on mount,
         not the shared loadData podcasts), so the v2-smoke / v2-audio / v2-data-edge
         oracle passes on both builds. Live audio routes through hls_engine
         (attachLiveStream/detachLiveStream); the m4a bypass stays native. -->
    <v2-player
      v-else-if="isReady && version === 2"
      :mods-list="v2Player.mods"
      :dark-theme="v2Player.useDarkTheme"
      :station="v2Player.station"
      :api-resource-url="v2Player.apiResourceUrl"
      :api-prefix="apiPrefix"
      :drop-to-live="v2Player.dropToLive"
      :use-m4a="true"
      @image-loaded="imageLoaded"
    ></v2-player>
    <div
      v-else-if="isReady"
      class="player"
      :data-version="version"
      v-bind="stubProps"
      style="min-height: 260px"
    ></div>
  </div>
</template>
<script>
import { getJson } from './utils/http';
import HlsLoader from './hls_loader';
import { urlGetter } from './utils/utils';
import {
  parseQueryString,
  parseCoreConfig,
  parseStations,
} from './utils/parseConfig';
import { fixKeyCase } from './utils/fixKeyCase';
import { buildPageList } from './utils/buildPageList';
import { parseTheme } from './utils/parseTheme';
import { buildUrl, buildShowMetadataUrl } from './utils/api';
import { sanitizeApiDomain } from './utils/apiDomain';
import { POST_API_DEFAULT } from './config/hosts';
import { placeholderImage } from './utils/placeholder';
// The ONE canonical station spelling. Imported here because this component supplies its
// own `station` key to pushTelemetryEvent (see emitLayoutTelemetry), and a caller-supplied
// key deliberately WINS over core/telemetry's module-level stamp — so the normalization
// has to happen at this call site too, not only inside setTelemetryStation.
import { normalizeStation } from './utils/station';
// P6-5 (MR-54): FORCE_V4 clamp + its no-op-safe telemetry hook.
import { FORCE_V4 } from './config/featureFlags';
// The widget's baked `<branch>-<sha7>` build identity, reported to Faro as
// app_version. Build-time constant, NOT a runtime fetch — see the module.
import { BUILD_VERSION } from './config/buildVersion';
import {
  pushTelemetryEvent,
  flushTelemetry,
  setTelemetryStation,
  setTelemetryHost,
} from './core/telemetry';
// The host-page origin resolver. `parentOrigin` (the postMessage targetOrigin) and
// the telemetry stamp both read the SAME computed, which funnels through this one
// helper — so telemetry and the resize handshake genuinely cannot disagree about
// which page the widget believes it is embedded in. The earlier arrangement made
// that claim while running two resolvers over two different inputs; independent
// review measured them disagreeing.
import { originFromCandidates } from './utils/resize';
// F-5 (MR-70): Faro RUM, statically imported (READINESS FIX A5 — a dynamic
// import('./core/faro') would either force inlineDynamicImports or emit a
// second chunk, either way failing P6-1's exactly-one-bundle gate). Bot-gated
// + runtime-no-op-when-unconfigured, so importing it here costs bundle size
// (covered by P6-1's gzip budget) but never forces network activity.
import { isSyntheticAgent } from './core/syntheticAgent';
import { initFaro, deriveFaroConfig } from './core/faro';
import { resolveFaroCollectorHost } from './config/hosts';
// MR-43 (P4-8): light the real v4 subtree in the Vue3 build entry.
import V4Player from './v4-components/Player.vue';
// LP-1 (MR-74): light the real v1 podcast-list subtree (faithful Vue1→Vue3 port
// of src/components/*). LP-3 (MR-77): light the real v3 gallery player (faithful
// Vue1→Vue3 port of src/v3-components/*). v2 keeps its PlayerStub until LP-2.
import PodcastList from './components/podcast_list';
import Categories from './components/categories';
import V3Player from './v3-components/Player.vue';
// LP-2 (MR-76): light the real v2 live now-playing player (faithful Vue1→Vue3
// port of src/v2-components/*, live-audio routed through hls_engine).
import V2Player from './v2-components/V2Player.vue';

// Every version's real artwork node, shared by the layout beacon's measurement
// and by the settle-wait that gates it.
export const ARTWORK_SELECTOR = [
  '.item-image', // v1
  '.post-webwidget-v2-now-playing-image-wrapper img', // v2
  '.player__image', // v3 classic + mini
  '.main-image', // v4
].join(', ');

// The beacon fires on the 200ms resize debounce + 2 rAFs. Measured against the
// DEPLOYED build on an iPhone-13 viewport, v4's `.main-image` is absent at
// 200ms, 19px at 400ms, and only reaches its settled 240px from ~800ms on — so
// the beacon read it at 0 and reported `artwork_h=0` with `artwork_found=true`,
// making `art_dominates` false on every v4 mobile embed. That is precisely the
// #399 signal (artwork broken on mobile) blind on precisely the #399 case, and
// v4 is the default version. Wait for the node to report a height before
// snapshotting, bounded so an image that never loads still produces a beacon
// (with artwork_h=0, which is then the truth rather than a timing artifact).
const ARTWORK_SETTLE_POLL_MS = 150;
const ARTWORK_SETTLE_MAX_TRIES = 20; // ~3s ceiling
// How long to wait for the two deferral frames below before concluding the
// browser is simply not running them. A page that is being painted delivers
// both within ~2 frames (~33ms at 60fps, ~66ms at 30), so a full second cannot
// pre-empt the rAF path on any compositing window — while a window the browser
// has stopped painting, where rAF never fires at all, still gets to beacon.
const LAYOUT_RAF_FALLBACK_MS = 1000;

export default {
  // MR-43 (P4-8): real v4 Player for version 4; LP-1 (MR-74) wires the real v1
  // podcast-list + categories rail; LP-3 (MR-77) wires the real v3 gallery
  // player; LP-2 (MR-76) wires the real v2 live now-playing player.
  components: {
    V4Player, PodcastList, Categories, V3Player, V2Player,
  },
  props: ['href'],
  data() {
    return {
      apiDomain: POST_API_DEFAULT,
      version: 4,
      // The version the embed actually requested, pre-FORCE_V4-clamp — carried
      // for the widget_layout telemetry beacon (usually === version; differs only
      // when the clamp fires). Default mirrors `version`.
      requestedVersion: 4,
      // The iframe's window.name is the embedder-assigned uuid; the resize
      // handshake stamps it as the message `id` (P3-8: moved from module scope
      // into the instance so it's per-instance and unit-testable).
      uuid: window.name,
      isReady: false,
      // MR-43: gates the stub -> real-v4-Player swap; set true at the end of
      // loadData's success so the real player mounts only once podcasts exist.
      contentReady: false,
      containerWidth: 700,
      displayPlayerInline: true,
      dropToLive: false,
      pageList: [],
      podcasts: [],
      categories: [],
      isDarkTheme: false,
      v2Player: {
        enabled: false,
        useDarkTheme: false,
        mods: '',
        station: '',
        dropToLive: false,
        apiResourceUrl: '',
      },
      v3PlayerConfigs: {
        mode: 'mini', // classic/mini mode
        theme: {
          // light/dark/custom theme colors
          type: 'light',
          background: null,
          text: null,
          accent: null,
        },
        preferences: {
          enableAlbumArt: true,
          enableHashtags: true,
          enableShowNotes: true,
          enableGallery: true,
          galleryLimit: 7, // total number of gallery episodes to display
        },
        zone: null, // social media connection
        autoplay: 'next', // action after episode finishes continue/popup/none/autoplay
        dropToLive: false, // boolean to play live stream after episode ends
        useM4a: true, // boolean for using m4a audio. This should be hardcoded to true
      },
      v4PlayerConfigs: {
        vertical: false,
        // D-211: the "Full"/"Single" embed toggle, and the discriminator that
        // decides whether an ABSENT toggle can be read as "Single" at all.
        // Both are assigned in processQueryParams; see Player.vue's
        // `showEpisodeList` for why the second one has to exist.
        full: false,
        singleEpisode: false,
        verticalEmbed: false,
        // 🔴 D-216 — NEVER ASSIGNED, BUT GENUINELY RENDERED FROM, AND THAT IS THE OPEN HALF.
        // `Object.assign(this.v4PlayerConfigs, themeConfig.v4Colors)` below carries only the
        // TOP-LEVEL background/play_button/start_remaining — which is why the hex overrides
        // work — so this nested object never leaves the literal. Meanwhile it IS rendered
        // from, at four live bindings across three components: Controls/Scrubber.vue's
        // `barColor()` picks the track colour off `theme.type`; Controls/PlayPause.vue emits
        // `play-pause__operator--${theme.type}` as a CSS class at TWO separate bindings;
        // Controls/Loader.vue sets `color: theme.text`.
        //
        // 🪤 THE EARLIER WORDING OF THIS LIST WAS WRONG IN BOTH DIRECTIONS, and review
        // measured it. It omitted PlayPause.vue's second class binding, and it counted
        // PlayPause.vue's `fill()` computed — which reads `theme.type` AND `theme.text` but
        // is referenced by NOTHING (v4's SVG getters hardcode `configs.play_button`, where
        // v3's use `this.fill`). `fill()` is a FOURTH dead computed of exactly the shape
        // this change deletes; it is left in place deliberately, because it is one of only
        // two readers of `theme.text` and removing a reader would change the shape of the
        // open decision below. The material correction: `theme.text` has ONE live reader
        // (Loader.vue), not two — which makes "delete the dark branches" cheaper than a
        // four-site count suggests.
        //
        // Their prop validators accept `['light','dark','custom']`, and Scrubber treats
        // `custom` exactly like `dark` — so THREE values are unreachable, not one, and
        // `custom` is the one that would logically pair with the hex-override path.
        //
        // So a v4 embed asking for `theme=dark` silently renders light, and the code that
        // would have honoured it was written and is unreachable. It is NOT deleted here
        // because both fixes change what a customer sees — wiring it flips live
        // dark-requesting embeds to dark; deleting the dark branches makes the no-op
        // permanent and admitted. That is a product decision. Carried as the one EXEMPT key
        // in test/unit/v4-configs-dead-key-census.spec.js so it cannot be lost again.
        theme: {
          type: 'light',
          background: null,
          text: null,
          accent: null,
        },
        shows: [],
        autoplay: 'next', // action after episode finishes continue/popup/none/autoplay
        dropToLive: false, // boolean to play live stream after episode ends
      },
      smc: null,
      mods: null,
      queryParams: null,
      zone: null,
      limit: null,
      page: null,
      perPage: null,
      next: null,
      theme: null,
      shared: null,
      singlePlaylistId: null,
      sharedStation: '',
      stations: null,
      dataRequest: null,
      hashTagModalIsVisible: false,
      currentCategory: null,
    };
  },
  mounted() {
    if (!this.queryParams) {
      this.queryParams = this.extractQueryParams();
      this.processQueryParams();
      this.populateDataRequest();
    }
    this.informResize = this.debounce(this.informResize, 200);
    // Take the FIRST container measurement synchronously, before installing the
    // debounced version. `containerWidth` is seeded at 700 in data(), and v1's
    // `wideView` computed flips at >= 590 — so with the first measure deferred
    // 200ms, EVERY mobile v1 list load painted 2-up and reflowed to one column
    // ~120-200ms later. Measured 4/4 items `.wide-view` at attach, second item
    // 138px at a 320px viewport, reproducing 3/3. The debounce exists to
    // rate-limit resize storms; it was never needed for the initial read.
    const debouncedUpdateContainerWidth = this.debounce(this.updateContainerWidth, 200);
    this.updateContainerWidth();
    // Only NOW swap in the debounced version, so every later resize is
    // rate-limited while the first measurement above stayed synchronous.
    this.updateContainerWidth = debouncedUpdateContainerWidth;
    // Held on the instance so beforeUnmount can actually remove it — an inline
    // arrow is unremovable, and a leaked resize listener keeps a destroyed
    // component reachable for the life of the frame.
    this.onWindowResize = () => this.updateContainerWidth();
    window.addEventListener('resize', this.onWindowResize, false);
    this.loadData(this.dataRequest);
    this.isReady = true;
  },
  // Nothing used to release the window-scoped work `mounted()` installs, so a
  // pending 200ms debounce outlived the component and fired into whatever was
  // left. In a browser that is a wasted no-op; under vitest, which tears the
  // happy-dom window down per test FILE without unmounting the components in
  // it, `window` is simply gone by then and the callback throws an unhandled
  // ReferenceError that fails the run — nondeterministically, since it is a race
  // between the timer and teardown (pipeline 23334 failed, 23333 passed, same
  // commit). Cancel the timers and drop the listener.
  beforeUnmount() {
    if (this.onWindowResize) {
      window.removeEventListener('resize', this.onWindowResize, false);
      this.onWindowResize = null;
    }
    if (this.informResize && this.informResize.cancel) this.informResize.cancel();
    if (this.updateContainerWidth && this.updateContainerWidth.cancel) {
      this.updateContainerWidth.cancel();
    }
    // The artwork settle-wait is a self-rescheduling timer — exactly the shape
    // of leak this hook exists to prevent. Do not let it outlive the component.
    if (this._artworkSettleTimer) {
      clearTimeout(this._artworkSettleTimer);
      this._artworkSettleTimer = null;
    }
    if (this._layoutRaf && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(this._layoutRaf);
      this._layoutRaf = null;
    }
    // The rAF backstop is a plain timer, so cancelAnimationFrame above does not
    // touch it — and it is exactly the shape of leak this hook exists to
    // prevent: it outlives the component by up to a second and fires into a
    // torn-down environment.
    if (this._layoutRafFallback) {
      clearTimeout(this._layoutRafFallback);
      this._layoutRafFallback = null;
    }
    // Stops a queued frame from starting a fresh chain after teardown.
    this._settleChainActive = false;
    this._layoutTelemetrySent = true;
  },
  computed: {
    // D-236 (A1): the region's accessible name, kept identical to the iframe
    // `title` the embedder sets. `classes/widget.js` cannot import this (it runs on
    // the HOST page, in the other bundle) and this cannot import that, so the two
    // are held together by `test/unit-vue/a11y-frame-and-region-named.spec.js`, which
    // derives the expected string from `frameTitle` itself rather than restating
    // it. A shared constant would need a third module imported by both bundles for
    // one string; the spec is the cheaper coupling and it is the one that fails
    // loudly.
    //
    // Reads the raw query param, not `stations[0]`. `stations` is not resolved until
    // `processQueryParams` runs and can stay null for the life of a failed load, and a
    // landmark whose name depends on a network fetch is a landmark that is sometimes
    // nameless — the exact defect being fixed.
    //
    // 🪤 It is still not qualified on the very first synchronous render: `queryParams`
    // is populated in `mounted()`, which runs after it. The property that matters is
    // that the region is NEVER nameless — the fallback covers that window — and it is
    // station-qualified from the first patch onward. Both halves are pinned in
    // `test/unit-vue/a11y-frame-and-region-named.spec.js`; do not "fix" the window by
    // re-resolving the query string here, which would put a second copy of
    // `extractQueryParams`' document.location-then-href precedence in the tree.
    regionLabel() {
      const station = this.queryParams && this.queryParams.station
        ? String(this.queryParams.station).trim() : '';
      return station ? `Audio player — ${station}` : 'Audio player';
    },
    apiPrefix() {
      return `https://${this.apiDomain}`;
    },
    // Origin of the host page that's embedding us. Used as the
    // targetOrigin for window.parent.postMessage, instead of '*' — see
    // REVIEW.md §2 P1. Resolves from the `src` query param the embedder
    // (classes/widget.js:28) appends, then falls back to document.referrer.
    // Returns '' if neither is parseable; informResize will skip posting
    // rather than broadcast to '*'.
    parentOrigin() {
      // NB no trailing comma in this argument list — the eslint parser this repo
      // uses rejects them (ES2017 syntax; build.target is es2015).
      return originFromCandidates(
        this.queryParams && this.queryParams.src,
        typeof document !== 'undefined' ? document.referrer : ''
      );
    },
    // P3-9 (MR-36): the shell↔player prop contract, exposed on the Phase-3
    // PlayerStub as `data-*` observables so the oracle can confirm the shell
    // computes + passes each version's expected props. Each key mirrors the
    // real player prop it will feed once Phase 4 wires the components (see
    // .attic/VUE3_MIGRATION_PLAN.md "PHASE-4 HANDOFF"). Object/array props are pinned
    // as counts/scalars (the stub can't carry the objects); the real players
    // receive the live objects. Keyed by `version` — one stub per version.
    stubProps() {
      // NB: no object spread — the pinned ESLint/espree (ES2015) can't parse it.
      const apiPrefix = this.apiPrefix;
      switch (this.version) {
        // v4/v3-player: :podcasts :configs :api-resource-url :api-prefix
        //               :categories @switch-category. (data-dark-theme here is a
        //               DERIVED observable of the PARSED theme param, not of anything
        //               a player receives. 🪤 v3 genuinely receives dark via
        //               :configs.theme.type; v4 does NOT — v4PlayerConfigs.theme is
        //               never assigned (D-216 / PS-48) — so on v4 this observable pins
        //               the PARSE, not the render, and the MR-35 theme=dark check it
        //               folds is satisfied without v4 rendering anything differently.)
        case 4:
        case 3:
          return {
            'data-api-prefix': apiPrefix,
            'data-podcast-count': this.podcasts.length,
            'data-category-count': this.categories.length,
            'data-api-resource-url': this.v2Player.apiResourceUrl,
            'data-dark-theme': this.isDarkTheme,
          };
        // v2's former `case 2` stub-prop set is REMOVED (LP-2/MR-76): v2, like v1,
        // renders its real player unconditionally (`isReady && version === 2` — not
        // data-gated), so the stub never renders for version 2 and this case is
        // dead. (v3/v4 stay above — they ARE data-gated and still stub until
        // podcasts load.) The shell→<v2-player> prop wiring is now pinned directly
        // on the real component in stub-prop-contract.spec.js.
        // v1 podcast-list: :category :display-player-inline :drop-to-live :list
        //                  :page-list :smc :use-m4a :api-prefix @switch-category
        //     categories:  :categories
        default:
          return {
            'data-api-prefix': apiPrefix,
            'data-podcast-count': this.podcasts.length,
            'data-category-count': this.categories.length,
            'data-smc': this.smc,
            'data-drop-to-live': this.dropToLive,
            'data-display-player-inline': this.displayPlayerInline,
            // v1 podcast-list passes :use-m4a="true" hardcoded (like v2)
            'data-use-m4a': true,
          };
      }
    },
  },
  methods: {
    extractQueryParams() {
      let qs = null;
      if (document && document.location && document.location.search) {
        qs = document.location.search.split('?')[1];
      } else if (!!this.href) {
        qs = this.href.split('?')[1];
      }
      if (qs) {
        return parseQueryString(qs);
      }

      // eslint-disable-next-line no-console
      console.log('Error: Could not find query string parameters!');
      return {};
    },

    processQueryParams() {
      // Bounded to hosts we own — this value can be supplied by any visitor via
      // the host page's query string, which the loader appends verbatim. See
      // utils/apiDomain.js. One seam: this governs the playlist API, the Faro
      // collector, image URLs and the v4 media links together.
      this.apiDomain = sanitizeApiDomain(this.queryParams.apiDomain, this.apiDomain);

      // Core config + theme/color config are pure, unit-tested helpers
      // (P3-4 / P3-7). The v2/v3/v4 non-theme assembly below stays inline.
      // P6-5 (MR-54): FORCE_V4 clamps a legacy requested version to 4 — one
      // app-level lever covering Path A + Path B.
      const core = parseCoreConfig(this.queryParams, { forceV4: FORCE_V4 });
      const themeConfig = parseTheme(this.queryParams);
      this.version = core.version;
      this.requestedVersion = core.requestedVersion;

      // P6-5: when the clamp fired, emit the ORIGINAL requested version so the
      // legacy residual can be re-measured post-cutover (P6-17). Guarded no-op
      // until Faro inits (F-5/MR-70) — never a hard Faro dependency.
      if (core.version !== core.requestedVersion) {
        pushTelemetryEvent('requested_version', {
          requested_version: String(core.requestedVersion),
        });
      }
      this.zone = core.zone;
      this.smc = core.smc;
      this.limit = core.limit;
      this.page = core.page;
      this.perPage = core.perPage;
      this.next = core.next;
      this.theme = core.theme;
      this.isDarkTheme = themeConfig.isDarkTheme;
      this.mods = core.mods;
      this.shared = core.shared;
      this.singlePlaylistId = core.singlePlaylistId;

      // v3 configs
      this.v3PlayerConfigs.mode = this.queryParams.mode || 'mini';
      // Theme colors + preferences from parseTheme (P3-7). Object.assign
      // overwrites exactly the keys the inline block set (type/bg/text/accent
      // and the five preferences), leaving other config keys untouched.
      Object.assign(this.v3PlayerConfigs.theme, themeConfig.v3Theme);
      Object.assign(this.v3PlayerConfigs.preferences, themeConfig.v3Preferences);
      this.v3PlayerConfigs.zone = this.queryParams.zone;
      this.v3PlayerConfigs.autoplay = this.queryParams.autoplay || 'none';
      this.v3PlayerConfigs.dropToLive = this.next === 'continue';
      // 🔴 v4's half of the same wire, which was missing entirely. `dropToLive` was
      // derived from `next === 'continue'` for v1 (`this.dropToLive`), v2
      // (`v2Player.dropToLive`) and v3 (above) — while `v4PlayerConfigs.dropToLive`
      // was initialised `false` and never reassigned, so v4's drop-to-live was dead
      // config at the shell as well as inside the player. Both halves had to be
      // closed; either alone leaves the feature unreachable.
      //
      // 🪤 `v4PlayerConfigs.autoplay` is deliberately NOT wired from
      // `queryParams.autoplay` here. It is pinned to 'next', which is v4's autoplay
      // default, and v3's line above defaults to 'none' — so copying v3's shape would
      // silently turn v4's autoplay-next OFF for every embed that omits the param.
      // `next=continue` is the wire the other versions already share; that is the one
      // v4 joins.
      this.v4PlayerConfigs.dropToLive = this.next === 'continue';

      // D-211 — `full` reached the host page and stopped there. `classes/widget.js`
      // read it to pick the iframe's BOOT height (624 vs 224) and nothing inside
      // the iframe read it at all, so v4's "Next up" list rendered for every embed
      // and POST's "Single" embed code was indistinguishable from its "Full" one.
      // The boot height then evaporated too: informResize's generic branch posts
      // `container.scrollHeight` and the embedder applies it verbatim, so both
      // embeds converged on identical rendering within the debounce window.
      //
      // `singleEpisode` is the second half of the gate rather than a parameter of
      // its own: POST's single-episode generator emits no `full` for anybody, so
      // absence of the toggle cannot be read as a choice there. `id` is that
      // family's own discriminator — the horizontal zone embeds this fix changes
      // carry `zone` and no `id`. Widening it is the SAFE direction (it keeps
      // today's rendering); narrowing it strips a list somebody asked for.
      this.v4PlayerConfigs.full = core.full;
      this.v4PlayerConfigs.singleEpisode = !!core.singlePlaylistId;
      // The vertical family's discriminator. NOT themeConfig.vertical (assigned from
      // `parseTheme()` below, which drives LAYOUT). Since D-215 both read through
      // `pyBool` and agree on every spelling, but they answer different questions,
      // so the gate still must not key on the layout one -- see Player.vue's
      // `showEpisodeList` for why.
      this.v4PlayerConfigs.verticalEmbed = core.verticalEmbed;

      if (this.shared) {
        this.sharedStation = this.queryParams.station;
      }

      this.stations = this.createStationInformation();

      // Stamp the station onto every telemetry event, HERE rather than inside
      // the async Faro init below (independent review of !219, D-19).
      //
      // Why here, stated accurately. An earlier revision of this comment gave
      // as its first reason that "events emitted synchronously earlier in boot
      // (requested_version, during processQueryParams) are BUFFERED and stamped
      // at push time, so setting this after the fetch resolves would leave them
      // unattributed." That reason is SELF-REFUTING and independent review
      // measured it: stamping happens at PUSH time, so an event already pushed
      // is already stamped — moving this call earlier in the same synchronous
      // function cannot retroactively attribute it.
      //
      //   PROBE requested_version attrs = {"requested_version":"1"}   <- no station
      //   PROBE later-event       attrs = {"reason":"no_engine","station":"wsaq"}
      //
      // `requested_version` — the P6-17 cutover re-measure, and the one event
      // that comment named — ships UNATTRIBUTED. Attributing it would mean
      // resolving stations before processQueryParams, which is a different
      // change; it is not what this line does.
      //
      // What the position genuinely buys: everything pushed AFTER it —
      // widget_layout, the hls_engine failure events, and anything emitted once
      // the player is live — is attributed, and it is set outside the async
      // Faro init so synthetic agents get it too (they never init Faro, so
      // nothing flushes, but a bot embed that starts reporting one day should
      // not silently report station-less events).
      //
      // Deliberately not Faro's `user.attributes.station` — that carries only
      // stations[0], so a multi-station embed misattributes every event to the
      // first station. This is the same value, but callers can override it.
      setTelemetryStation(this.stations[0] && this.stations[0].station);

      // The embedding site, stamped onto every event alongside the station. Set
      // here, beside the station, and BEFORE initFaro for the same reason: events
      // buffered before Faro is ready are stamped at push time, so anything emitted
      // between now and init carries the host too.
      //
      // 🔑 ORIGIN, NOT THE FULL HREF — the host page's query string carries session
      // tokens and addresses (see the DISCLOSURE note in core/telemetry.js). The
      // property that the value stamped here is an ORIGIN is gated in
      // test/unit-vue/faro-wire.spec.js ('never stamps the full href'), which mounts
      // this component with a host href whose path and query are distinctive and
      // asserts neither reaches the stamp. Before that spec existed, replacing this
      // line with `setTelemetryHost(window.location.href)` left all 1465 tests green.
      //
      // 🔑 `this.parentOrigin` — the SAME value the resize handshake posts to, not a
      // second resolver reading a second input. The earlier arrangement claimed the
      // two "can never disagree" and independent review measured them disagreeing.
      // One computed, one consumer contract; see utils/resize.js originFromCandidates.
      //
      // Guarded because this runs in happy-dom/node under test and in synthetic-agent
      // embeds; `parentOrigin` itself never throws, but a future edit to it might.
      try {
        setTelemetryHost(this.parentOrigin);
      } catch (e) {
        /* telemetry must never surface to the embed */
      }

      // F-5 (MR-70): init Faro once apiDomain + station are both known. Bot
      // traffic (Pingdom/headless/known crawlers) never reaches initFaro at
      // all — the faro chunk isn't even fetched network-wise for it, since
      // Faro rides in this same IIFE bundle regardless (readiness A5) but its
      // *initialization* (and therefore its network activity) is gated here.
      // `deriveFaroConfig` returns an empty faroUrl for a falsy apiDomain
      // today (F-4) — the `if (faroUrl)` guard is defensive for a future
      // build-time opt-out, mirroring postv2's `FARO_URL=''` escape hatch,
      // which is still not wired. (This used to add "out of scope — no vite
      // config touch here"; the build-identity define now lives in
      // vite.main.config.mjs, so that parenthetical is no longer true. The
      // escape hatch itself remains unbuilt, which is what the guard is for.)
      if (!isSyntheticAgent(typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
        const station = this.stations[0] && this.stations[0].station;
        // 🔑 THE BUILD IDENTITY IS BAKED IN, NOT FETCHED — see src/config/buildVersion.js
        // for the full post-mortem. This was an async `fetch('/WEBWIDGETV2-VERSION.txt')`
        // whose marker is DEPRECATED (S2/S3 superseded it with widget-version.json) and
        // which 404s on both prod hostnames anyway: MEASURED 2026-08-20, `app_version=dev`
        // on 107,231 of 107,233 RUM events and 14,084 fetches of a 404 page. Because
        // initFaro was chained onto that promise, RUM also started p90 551.7ms late on every
        // load. Baking the version removes the request, the wait and the wrong label at
        // once, and makes this whole block SYNCHRONOUS.
        //
        // Terminal try/catch, not a promise `.catch`: initFaro is the real Grafana SDK and
        // a throw here must never surface on the host page (telemetry's "never surface to
        // the embed" rule) — the same guarantee the old trailing `.catch(() => {})` gave.
        try {
          // 🔑 The COLLECTOR follows the origin this widget was SERVED from, not apiDomain —
          // see resolveFaroCollectorHost. A development-CDN widget must report to avow, not
          // to production, and the origin is what the collector's CORS allowlist keys on.
          const collectorHost = resolveFaroCollectorHost(
            typeof window !== 'undefined' && window.location ? window.location.hostname : ''
          );
          const { faroUrl, environment, version } = deriveFaroConfig(collectorHost, BUILD_VERSION);
          if (faroUrl) {
            // `hostOrigin` rides `user.attributes` alongside `station` — see the
            // ~20-of-22 note in core/faro.js. Without it the field reaches only
            // the four hand-pushed events and the exception stream carries none,
            // which is exactly what the station fix in MR-72 was for.
            initFaro(faroUrl, {
              version, environment, station, hostOrigin: this.parentOrigin,
            });
            // Drain any telemetry (e.g. requested_version) emitted synchronously
            // EARLIER in this same tick — processQueryParams runs before this block and
            // pushes before window.faro exists. Still required now that init is
            // synchronous: "synchronous" removes the network wait, not the ordering.
            // Without this the event is dropped every load and the P6-17 cutover
            // re-measure goes blind.
            flushTelemetry();
          }
        } catch (e) {
          /* telemetry must never surface to the embed */
        }
      }

      // Show metadata feeds the v4 PlayerMenu's RSS-subscribe action — no
      // other version reads it. Skip the request on v1/v2/v3 (REVIEW.md
      // §1 P1) and don't let a network failure silently produce an
      // unhandled rejection.
      if (this.version === 4) {
        // MR-50 (P5-10): getJson does the fetch + 200-check + .json() in one step
        // (absorbs the old `.then(r => r.json())`). Behaviour note: the old blind
        // `response.json()` parsed ANY status's body and assigned it to `shows`
        // (a 4xx/5xx error body included); getJson instead rejects every non-200,
        // so the .catch below leaves `shows` at its [] default. This is a DIFFERENT
        // (narrower) failure surface, strictly safer here — the Menu guard tolerates
        // [] and would mis-handle a garbage error-body object anyway.
        getJson(buildShowMetadataUrl(this.apiPrefix, this.stations[0].station))
          .then((data) => {
            this.v4PlayerConfigs.shows = data;
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('Failed to load show metadata; RSS subscribe will be unavailable:', err && err.message);
            // shows defaults to [] in data(); the `Array.isArray(this.shows)`
            // tolerance in Menu.vue `subscribeRSS` handles that.
          });
      }

      this.displayPlayerInline = core.displayPlayerInline;
      this.dropToLive = core.dropToLive;

      this.v2Player = {
        enabled:
          this.queryParams.version &&
          parseInt(this.queryParams.version, 0) === 2,
        useDarkTheme: this.theme === 'dark',
        mods: this.queryParams.v2_mods,
        station: this.queryParams.station,
        dropToLive: this.next === 'continue',
        apiResourceUrl: buildUrl(this.apiPrefix, {
          station: this.queryParams.station,
          zone: this.zone,
          limit: this.limit,
          page: this.page,
          perPage: this.perPage,
          id: this.singlePlaylistId,
        }),
      };

      // v4 player colors + vertical from parseTheme (P3-7)
      Object.assign(this.v4PlayerConfigs, themeConfig.v4Colors);
      this.v4PlayerConfigs.vertical = themeConfig.vertical;
    },

    createStationInformation() {
      return parseStations(this.queryParams, this.zone);
    },

    populateDataRequest() {
      this.dataRequest = this.requestAll(null, null);
      // Inject the hls.js live engine (window.Hls) — the single live engine every
      // player version now uses. (The retired Viblast global's loader was dropped
      // in LP-4/MR-78 once v1/v3 migrated onto hls.js alongside v2/v4.)
      HlsLoader.load();
    },

    requestAll(pageNumber, category) {
      const isMultiple = this.stations.length > 1;
      return Promise.all(
        this.stations.map((station) => {
          if (category && category.station !== station.station) {
            return {
              playlists: [],
              playlist_categories: [],
            };
          }
          const url = buildUrl(this.apiPrefix, {
            station: station.station,
            zone: station.zone,
            shared: this.shared,
            limit: this.limit,
            page: isMultiple ? null : pageNumber || this.page,
            perPage: isMultiple ? null : this.perPage,
            category: category ? category.id : null,
            id: this.singlePlaylistId,
          });

          return getJson(url);
        })
      );
    },

    debounce(fn, wait) {
      let timeout;
      const debounced = (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          fn.apply(this, args);
        }, wait || 100);
      };
      // beforeUnmount needs a handle on the pending timer; without one the
      // trailing call is unstoppable once scheduled.
      debounced.cancel = () => clearTimeout(timeout);
      return debounced;
    },

    updateContainerWidth() {
      const el = this.$el || this.$refs.container;
      if (el && el.scrollWidth !== this.containerWidth) {
        this.containerWidth = el.scrollWidth;
        // eslint-disable-next-line no-console
        // console.log('New width:', this.containerWidth);
        this.informResize();
      }
    },

    loadData(allreqs) {
      allreqs.then((datalist) => {
        const pageList = [];

        let allcategories = [];
        let allpodcasts = [];
        let currentCategory = '';
        let sharedpodcast = null;

        this.stations.forEach(
          (station, stni) => {
            const data = datalist[stni];
            // PS-1 (MR-A): the raw POST widget.json emits category ids as NUMBERS
            // in `playlists[].categories`, but this rail filter compared them
            // against `c.id.toString()` (a STRING), so `[1,2,…].includes("1")`
            // missed for every category → the "more categories" rail never
            // rendered for real (numeric-id) data. Normalise the first playlist's
            // ids to Number here and coerce `c.id` to Number in the filter — the
            // SAME numeric type the per-item `+cat.id === +id` resolution below
            // uses — so string- and numeric-id payloads resolve identically.
            //
            // PS-1 hardening (MR-A fold): defend the Number() normalisation.
            // `[NaN].includes(NaN)` is true (SameValueZero), so a non-numeric id
            // on BOTH sides would over-match — drop NaN from the id set and skip
            // NaN candidates so only genuine numeric ids ever match. Also tolerate
            // an absent `data.playlists[0].categories` (|| []) so a malformed first
            // playlist can't throw on `.map`. Integer PKs make this unreachable in
            // prod; the behaviour on real numeric data is byte-identical.
            const firstPlaylistCategoryIds = data.playlists.length > 0
              ? (data.playlists[0].categories || [])
                .map(Number)
                .filter(n => !Number.isNaN(n))
              : [];
            const categories = data.playlists.length > 0
              ? data.playlist_categories
                .filter((c) => {
                  const n = Number(c.id);
                  return !Number.isNaN(n) && firstPlaylistCategoryIds.includes(n);
                })
                .map(fixKeyCase)
                .map((c) =>
                  Object.assign(c, {
                    station: station.station,
                  })
                )
              : [];
            const podcasts = [];
            const playlists = data.playlists;

            let podcast;
            if (playlists.length === 0) {
              const img = placeholderImage(700, 366, station.station);
              podcast = {
                showTitle: station.station,
                name: 'Episode Title',
                shared: false,
                aacFile: '',
                categories: [],
                connections: (0),
                count: 0,
                description: 'Please upload a podcast episode to POST to view the Web Widget.',
                duration: 0,
                externalUrl: '',
                id: 0,
                image: img,
                m4aFile: '',
                originalImage: '',
                previewUrl: '',
                shareImage: '',
                shortUrl: '',
                showId: 0,
                squareImage: '',
                timestampCreated: Math.floor(Date.now() / 1000),
                timestampUpdated: Math.floor(Date.now() / 1000),
              };
              // this.v3PlayerConfigs.preferences.enableAlbumArt = false;
              // this.v3PlayerConfigs.preferences.enableGallery = false;
              // this.v3PlayerConfigs.preferences.enableHashtags = false;
              podcasts.push(podcast);
            } else {
              for (let i = 0; i < playlists.length; i++) {
                podcast = fixKeyCase(playlists[i]);
                podcast.categories = podcast.categories.map((id) => {
                  let found = null;
                  categories.forEach((cat) => {
                    if (+cat.id === +id) {
                      found = cat;
                    }
                  });
                  return found;
                });
                if (podcast.image) {
                  podcast.image = urlGetter(this.apiDomain, podcast.image);
                }
                podcast.station = this.sharedStation || station.station;

                if (podcast.id === this.shared && !sharedpodcast) {
                  // eslint-disable-next-line no-console
                  console.log(
                    'Detected shared content:',
                    podcast.id,
                    podcast.name
                  );
                  podcast.shared = true;
                  sharedpodcast = podcast;
                } else {
                  podcast.shared = false;
                  podcasts.push(podcast);
                }
              }
            }

            // Pagination entries for this station's data (P3-6: extracted to
            // buildPageList); accumulated across stations into pageList as before.
            pageList.push(...buildPageList(data));

            allcategories = allcategories.concat(categories);
            allpodcasts = allpodcasts.concat(podcasts);
            currentCategory = data.current_category;
          },
          (data, xhr) => {
            // eslint-disable-next-line no-console
            console.log('Unable to load:', data, xhr);
          }
        );

        allpodcasts = allpodcasts
          .sort((a, b) => {
            if (a.timestampCreated < b.timestampCreated) {
              return -1;
            }
            if (a.timestampCreated > b.timestampCreated) {
              return 1;
            }
            return 0;
          })
          .reverse();

        /**
         * Version 3 requires all podcasts loaded for the search filter to work properly
         * All other versions should limit the number of podcasts they display
         */
        if (this.version !== 3) {
          allpodcasts = allpodcasts.slice(0, this.limit);
        }

        if (sharedpodcast) {
          allpodcasts.unshift(sharedpodcast);
        }

        this.currentCategory = currentCategory;
        this.pageList = pageList;

        this.podcasts = allpodcasts;
        if (this.categories.length === 0) {
          this.categories = allcategories;
        }
        this.contentReady = true;
      }).catch((err) => {
        // requestAll's Promise.all rejects when any station's getJson rejects.
        // getJson resolves only on HTTP 200; the pegasus get() it replaced treated
        // the whole 200–399 band as success, so this NARROWS the success set — a
        // valid-JSON 2xx-non-200 (201/202/203/206) that pegasus would have RENDERED
        // now degrades here to the empty state. That case does not occur on these
        // station GET endpoints (they answer 200), so it is a deliberate ~nil-risk
        // narrowing, not a strictly-preserved behaviour. For the cases that DO
        // occur the paths land identically: a 4xx/5xx/0 rejects under both; and a
        // malformed-200 rejects here where pegasus resolved `null` and the
        // `data.playlists` deref below then threw INTO this same .catch. Without
        // this catch a single failing station shows up as an unhandled promise
        // rejection in the host page's console. Leave podcasts as the default empty
        // array so the v4 inline-search / v3 gallery / v1 list all degrade to an
        // empty state rather than crash on undefined.
        // eslint-disable-next-line no-console
        console.warn('Failed to load playlist data:', err && err.message);
      });
    },

    switchCategory(category) {
      if (category === null) {
        this.loadData(this.requestAll(1, null));
        return;
      }
      let categ;
      this.categories.forEach((cat) => {
        if (cat.name === category.name) {
          categ = category;
        }
      });
      this.loadData(this.requestAll(1, categ));
    },

    switchPage(pageNumber, currentCategory) {
      if (currentCategory === null) {
        this.loadData(this.requestAll(pageNumber, null));
        return;
      }
      this.loadData(this.requestAll(pageNumber, currentCategory));
    },

    imageLoaded() {
      // The AUTHORITATIVE "a cover's pixels have arrived" signal. v1 has always
      // called this (podcast.vue -> podcast_list); !209 wired v4 and v2 to it too.
      // The layout beacon latches on this instead of guessing, because guessing
      // demonstrably failed twice — see emitWhenArtworkSettles.
      this._artworkSignalled = true;
      this.informResize();
    },

    // The other terminal state of a cover: it never arrives (404, or an ad-blocker
    // aborting it). v1 releases its box reservation there too, which moves ~910px of
    // layout, so the height has to be re-posted — but `_artworkSignalled` is deliberately
    // NOT set. That flag means "artwork_h is a settled READING", and a cover that failed
    // gives nothing to read: stamping it would certify a measurement of an absent image on
    // the one beacon whose artwork distribution the reservation was chosen from.
    imageFailed() {
      this.informResize();
    },

    informResize() {
      // v3/v2 measure their own player root when the REAL player is mounted.
      // Under the Phase-3 PlayerStub those version-specific selectors are absent,
      // so fall THROUGH to the generic container measurement below rather than
      // throwing (v2's unguarded deref) or posting nothing (v3) — matching
      // postToParent's fail-safe philosophy. Phase 4 restores the real players,
      // whose selectors resolve, so those builds hit their specific branch
      // unchanged. (The version-2 throw was reachable via contract facet 6#1's
      // effective version=2 embed but invisible to the top-frame pageerror hook.)
      if (this.version === 3) {
        const player = this.$el.querySelector('.player');
        if (player) {
          const msg = JSON.stringify({
            id: this.uuid,
            type: 'resize',
            height: player.offsetHeight,
            width: player.offsetWidth,
          });
          this.postToParent(msg);
          this.scheduleLayoutTelemetry();
          return;
        }
      }

      if (this.version === 2) {
        const layout = this.$el.querySelector('.post-webwidget-v2-player-layout');
        if (layout) {
          const msg = JSON.stringify({
            id: this.uuid,
            type: 'resize',
            height: layout.offsetHeight,
            width: layout.offsetWidth,
          });
          this.postToParent(msg);
          this.scheduleLayoutTelemetry();
          return;
        }
      }

      const container = (this.$refs && this.$refs.container) || this.$el;
      if (!container) return;

      const msg = JSON.stringify({
        id: this.uuid,
        type: 'resize',
        width: container.scrollWidth,
        height: container.scrollHeight,
      });
      if (this._lastMessage !== msg) {
        this._lastMessage = msg;
        this.postToParent(msg);
      }

      this.scheduleLayoutTelemetry();
    },

    // One-shot RUM layout snapshot (guarded once inside), deliberately AFTER
    // postToParent and deferred two frames: widget.js applies the posted height
    // to the iframe, so reading innerHeight before the post returns the
    // embedder's hardcoded DEFAULT (224/500/624), not the rendered box. Measured
    // before that move: viewport_h=224 against content_h 446-464 on every probe,
    // which made `content_h > viewport_h` match every v4 embed on the fleet
    // forever. The rAFs let the parent's resize land first.
    //
    // ⚠️ CALL THIS FROM EVERY `informResize` EXIT. Placing the emit inline after
    // postToParent put it below the v3 and v2 early returns, which fire on every
    // real v2/v3 embed — so the beacon went dead on two of the four versions.
    // That is the same defect 112671b fixed on v4 (a selector that missed the
    // dominant version), reintroduced from the other end.
    //
    // `typeof window` rather than a bare deref: the 200ms debounce that drives
    // informResize can outlive the environment, and a bare `window` there throws
    // an unhandled ReferenceError. See the beforeUnmount note.
    scheduleLayoutTelemetry() {
      if (typeof window === 'undefined') return;
      // ONE chain per mount. informResize runs many times per pageview (mount,
      // every window resize, every image-loaded, category switch, data reload)
      // and each run used to start an independent polling chain sharing ONE
      // `_artworkSettleTimer` slot and ONE try budget. Consequences: beforeUnmount
      // could only cancel the newest chain (the others outlived the component —
      // the exact leak that hook exists to prevent), and the ~3s ceiling was
      // really ~3s/N. A single chain already re-reads the DOM every 150ms, so a
      // later trigger has nothing to add.
      if (this._layoutTelemetrySent || this._settleChainActive) return;
      this._settleChainActive = true;
      if (typeof window.requestAnimationFrame === 'function') {
        // A browser stops running rAF for a window it is not COMPOSITING — a
        // backgrounded tab, another window on top. That is NOT `document.hidden`
        // and the page cannot detect it: measured in a real Chrome whose
        // visibilityState still read "visible", 188 setInterval ticks against 1
        // rAF frame in 5 seconds. With the whole chain hanging off these two
        // frames, the beacon then never fires at all — no error, no partial
        // event — which is indistinguishable from dead telemetry, and makes
        // fleet counts under-report non-randomly (background tabs are a normal
        // way to open station pages).
        //
        // So: frames first, timer as a backstop, whichever arrives first wins.
        // `handedOff` is a closure rather than instance state because
        // `_settleChainActive` already guarantees one chain per mount, and the
        // guard has to hold even though `_layoutTelemetrySent` is set by the
        // very emit being deferred here.
        let handedOff = false;
        const proceed = () => {
          if (handedOff) return;
          handedOff = true;
          if (this._layoutRafFallback) {
            clearTimeout(this._layoutRafFallback);
            this._layoutRafFallback = null;
          }
          this.emitWhenArtworkSettles();
        };
        // Store both handles: an uncancelled frame outlives beforeUnmount, and
        // the identifier `window` inside the callback is resolved AT FRAME TIME,
        // where it can already be gone — a bare deref there is the same
        // unhandled ReferenceError that failed pipeline 23334.
        this._layoutRaf = window.requestAnimationFrame(() => {
          if (typeof window === 'undefined') return;
          this._layoutRaf = window.requestAnimationFrame(proceed);
        });
        this._layoutRafFallback = setTimeout(proceed, LAYOUT_RAF_FALLBACK_MS);
        return;
      }
      this.emitWhenArtworkSettles();
    },

    // Hold the one-shot snapshot until the artwork node reports a height, so
    // artwork_h/art_dominates describe the rendered layout instead of the
    // mid-load one. See ARTWORK_SETTLE_* above for the measurement that forced
    // this. Bounded by tries rather than wall-clock: an embed whose cover never
    // loads still beacons, and there is no clock to read in a unit test.
    emitWhenArtworkSettles() {
      if (this._layoutTelemetrySent) return;
      if (typeof window === 'undefined') return;
      const el = (this.$refs && this.$refs.container) || this.$el;
      const art = el && el.querySelector ? el.querySelector(ARTWORK_SELECTOR) : null;
      // An ABSENT node is waited on too, not treated as terminal: at 200ms the
      // v4 subtree has not rendered at all, so "no artwork" and "artwork not yet
      // laid out" are the same state this early.
      const height = art && art.offsetHeight ? art.offsetHeight : 0;
      // No artwork node AT ALL once content has rendered is a terminal answer
      // (artwork_found=false), not something to wait ~3s for. v3 with albumart
      // off is the common case and used to burn the whole ceiling every load.
      const contentRendered = !!(this.podcasts && this.podcasts.length);
      // `>= 1` — the FIRST read is always too early (at ~233ms the subtree has not
      // rendered), so give it one poll before concluding there is genuinely no
      // artwork. The counter is incremented below, so this reads the prior count.
      const noArtworkToWaitFor = contentRendered && !art && this._artworkSettleTries >= 1;
      // ⚠️ DO NOT go back to "two equal consecutive reads". That was the second
      // wrong answer here and it shipped: the pre-load box is a STATIC ~19px
      // text line box (v4's `.image-placeholder`, sized only by `height: auto`),
      // so "stopped changing" is trivially true while nothing has loaded.
      // MEASURED on the deployed build, delaying only the cover:
      //     cover at   0ms -> artwork_h=240  (correct)
      //     cover at 900ms -> artwork_h=19   (wrong; true height 240)
      //     cover at2000ms -> artwork_h=19   (wrong; true height 240)
      // i.e. correct only when the cover beats one 150ms poll — never on the
      // cellular connections this beacon exists to diagnose. The earlier `> 0`
      // gate was wrong the same way, latching the same 19px.
      //
      // So latch on the LOAD EVENT, which !209 now provides on v1/v2/v4, and keep
      // polling only as the fallback for v3 (no notification) and for covers that
      // never arrive. The bounded ceiling still guarantees a beacon.
      const settled = this._artworkSignalled ? height > 0 : noArtworkToWaitFor;
      this._artworkSettleTries = (this._artworkSettleTries || 0) + 1;
      if (!settled && this._artworkSettleTries <= ARTWORK_SETTLE_MAX_TRIES) {
        // Single-line call on purpose: the legacy `npm run lint` parser predates
        // ES2017 and rejects a trailing comma in call arguments.
        const retry = () => this.emitWhenArtworkSettles();
        this._artworkSettleTimer = setTimeout(retry, ARTWORK_SETTLE_POLL_MS);
        return;
      }
      this._settleChainActive = false;
      this._artworkSettleTimer = null;
      this.emitLayoutTelemetry();
    },

    // Centralised target-origin handling for resize messages. If
    // parentOrigin can't be resolved we drop the message rather than
    // broadcast to '*' (REVIEW.md §2 P1). The host iframe still gets a
    // sensible default size from classes/widget.js:46-62; the only thing
    // missed is dynamic resize, which fails-safe.
    postToParent(msg) {
      const origin = this.parentOrigin;
      if (!origin) return;
      // Fail-safe: a debounced resize can fire after the host frame has
      // navigated/detached, or against a mismatched cross-origin parent —
      // either makes postMessage throw (a SecurityError on target-origin
      // mismatch). Drop it rather than surface an uncaught error (MR-70).
      try {
        window.parent.postMessage(msg, origin);
      } catch (e) {
        // intentional no-op — see the fail-safe note above
      }
    },

    // STUB (Draft MR): one-shot RUM layout snapshot for diagnosing real-device
    // layout defects (#399/#570-class) that do NOT reproduce in headless e2e — a
    // field beacon of the viewport / device / measured content geometry + the
    // derived defect flags (overflow, clipped_v, paint_uncovered,
    // art_dominates). Emitted via the existing no-op-safe telemetry hook, which
    // is bot-suppressed (a synthetic agent never inits Faro, so its buffered
    // events never flush). Fires exactly ONCE per mount, from the debounced
    // informResize.
    // ⚠️ ON THIS BRANCH ONLY v1 PRODUCES A SETTLE SIGNAL. The wait is gated on
    // an app-level imageLoaded burst, and only v1 has one (podcast.vue →
    // podcast_list → app.imageLoaded): v4 handles image load locally and never
    // notifies the shell, and v2/v3 have no hook at all. !209 adds the
    // `image-loaded` emit to v4, v2 and v3; until that lands, every v2/v3/v4
    // embed falls through the ~3s give-up path and reports
    // artwork_settled="false" — so a querier following this beacon's OWN advice
    // ("filter artwork_settled=true before trusting artwork_h or
    // art_dominates") discards the entire fleet except v1, v4 being the default
    // version. The geometry in those beacons is usually still real (3s is long
    // enough for a cover to paint) but it is unverified, which is exactly what
    // the flag is telling you. Measured, pre-settle-gate: a v1 probe reported
    // artwork_h="16" — the collapsed alt-text line box, not artwork.
    // ⚠️ One-shot per mount means ORIENTATION CHANGE is never captured: a user
    // who loads in portrait and rotates produces a beacon saying 'portrait', and
    // the landscape layout is never reported. For mobile layout defects rotation
    // is a first-order suspect, so this is a real gap in the field data.
    // All values are strings (Faro pushEvent attrs) → queryable in Loki as
    // event_data_*. NEVER throws (the whole read path is guarded) — telemetry must
    // never surface to the embed. ES2015 only (no ?./?? per the src ban).
    // Field set + emit timing reviewed 2026-07-27; the known limits are stated
    // inline. Confirm against obs before promoting beyond Draft.
    emitLayoutTelemetry() {
      if (this._layoutTelemetrySent) return;
      // Snapshot only once real content has rendered (something to measure).
      if (!this.podcasts || !this.podcasts.length) return;
      this._layoutTelemetrySent = true;
      try {
        const w = window;
        const vv = w.visualViewport;
        const scr = w.screen;
        // The DEVICE screen (readable cross-origin, unlike window.parent) — the
        // real "is this a phone / which orientation" signal. Distinct from…
        // 🔴 `screen` IS NOT A DEVICE SCREEN ON WEBKIT INSIDE AN IFRAME, and the
        // widget only ever runs inside one. Measured 2026-08-08 on the v1
        // single-episode @390 fixture, both engines, host page identical:
        //
        //   host window.screen    chromium [390,844]    webkit [390,844]
        //   beacon screen_w/h     chromium  390x844     webkit  358x366
        //
        // 358x366 is this frame's own box. So for Safari traffic — which is most
        // of the widget's traffic, and the whole reason this beacon exists —
        // `screen_w`/`screen_h` are a duplicate of `viewport_w`/`viewport_h`,
        // not device dimensions. Do NOT build a dashboard predicate on them, and
        // do not read a Safari `screen_*` as a device fact.
        //
        // Two things this does NOT break, deliberately checked: `art_dominates`
        // and `clipped_v` both divide by `innerH`, never by screen; and
        // `orientation` takes `screen.orientation.type` first, so the raw
        // `screenW >= screenH` arm is only reached on engines that have neither
        // that nor matchMedia. Kept and reported as-is rather than "corrected",
        // because a field that silently reports something else would be worse —
        // and because whether real Mobile Safari matches Playwright's WebKit
        // here is still an open device-pass question.
        const screenW = scr && scr.width ? scr.width : 0;
        const screenH = scr && scr.height ? scr.height : 0;
        // …the IFRAME's own box. innerWidth IS the embed column the host page
        // allotted us. innerHeight USED to be worthless: widget.js sets a
        // hardcoded initial iframe height (224/500/624) and applies the posted
        // height only when the resize message arrives, and this beacon ran
        // BEFORE postToParent in the same function — so viewport_h / visual_h
        // read the embedder's PRE-HANDSHAKE DEFAULT. Measured: viewport_h=224
        // against content_h 446-464 on every probe, which made
        // `content_h > viewport_h` match every v4 embed on the fleet forever.
        // scheduleLayoutTelemetry now runs it after the post, two frames later.
        // MEASURED, not merely reasoned: in the e2e harness, which drives the
        // REAL embedder (host page loads /futuri-post-widget.js), same fixture,
        // only this file differing —
        //     emit before the post: viewport_h=224 (the default), content_h=446
        //     emit after  the post: viewport_h=446 (the applied box), content_h=446
        // Pinned by the 'viewport_h reports the rendered box' e2e test, which
        // asserts against the iframe's actual box rather than the literal 446.
        // Still open: this is a desktop chromium measurement. On a real device
        // the pre-load caveat below (artwork measured before it loads) applies
        // to content_h/artwork_h, so confirm against obs once a build carrying
        // this beacon is deployed.
        const innerW = w.innerWidth || 0;
        const innerH = w.innerHeight || 0;
        // D-53. The two fields above are shipped VERBATIM and always will be — see the
        // block at `screenW`. This makes their unreliability machine-readable instead,
        // so a query can EXCLUDE the bad rows rather than average them in. Without it
        // the beacon reports Safari's frame box as a device screen and nothing
        // downstream can tell, which is the whole production consequence of D-53.
        //
        // 🔑 DERIVED FROM THE VALUES, NOT FROM THE ENGINE. No user-agent sniff and no
        // `isWebKit`: a screen that CONTAINS this frame must be bigger than it, so a
        // reported screen that is no larger in BOTH dimensions cannot be a device
        // screen — on any engine. That is what lets this ship while "does real Mobile
        // Safari behave like Playwright's WebKit" is still an open device-pass
        // question: the flag answers from what was measured, so it is right either way.
        //
        // 🪤 BOTH dimensions, deliberately. A desktop embed is routinely exactly as WIDE
        // as the screen while the screen is perfectly real — requiring width alone would
        // flag most desktop traffic and make the field useless. Height is what separates
        // them. A single-dimension anomaly is NOT flagged: zoom and devicePixelRatio can
        // produce one, and a noisy flag would be ignored, which is worse than no flag.
        //
        // 🪤 `unknown`, not `false`, when the screen is unreadable. `0 <= innerW` is
        // TRUE, so the naive predicate calls a missing screen a frame box and invents a
        // fact from absent information. Absence is not a verdict.
        let screenIsFrameBox = 'unknown';
        if (screenW && screenH) {
          screenIsFrameBox = (screenW <= innerW && screenH <= innerH) ? 'true' : 'false';
        }
        // Orientation of the DEVICE — NOT the iframe aspect, which is driven by
        // our own content height and would mislabel a short-but-wide embed,
        // which is most embeds.
        //
        // ⚠️ matchMedia used to be first, and that is exactly the mistake above:
        // `matchMedia('(orientation: portrait)')` evaluated INSIDE the iframe
        // answers for the IFRAME's viewport, not the device. Caught on the
        // deployed build — on a portrait iPhone 13 (390x844) v1 reported
        // 'landscape' (its box is 390x307) and v3 reported 'landscape' (390x385)
        // while v4 reported 'portrait' (390x590). The only variable was the
        // iframe's own aspect.
        //
        // `screen.orientation.type` is device-level and readable from inside an
        // iframe — verified on the deployed build: box 700x766 while
        // screen.orientation.type reads 'portrait-primary' off the 390x844
        // device. So it leads. matchMedia stays as the fallback for engines
        // without it (iOS < 16.4), where an iframe-scoped answer still beats
        // nothing, and the raw screen aspect stays LAST because iOS does not
        // swap screen.width/height on rotation.
        let orientation = 'unknown';
        if (scr && scr.orientation && scr.orientation.type) {
          orientation = scr.orientation.type.indexOf('portrait') === 0 ? 'portrait' : 'landscape';
        } else if (w.matchMedia) {
          orientation = w.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
        } else if (screenW && screenH) {
          orientation = screenW >= screenH ? 'landscape' : 'portrait';
        }
        // the same node informResize measures — `$el` alone becomes a comment
        // anchor if the root ever turns into a fragment, and scrollWidth would
        // silently coerce to 0, reporting overflow:'false' forever.
        const el = (this.$refs && this.$refs.container) || this.$el;
        const contentW = el && el.scrollWidth ? el.scrollWidth : 0;
        const contentH = el && el.scrollHeight ? el.scrollHeight : 0;
        // Every version's real artwork node. The first cut listed only
        // `.item-image, .player__image` — v1 and v3 — so on v4 (the DEFAULT
        // version and the dominant traffic), v2, and v3-mini the probe found
        // nothing and reported artwork_h="0" / art_dominates="false",
        // byte-identical to a perfectly-sized cover. A v4 cover regressing to
        // 400px on a 640px phone would have read as healthy.
        // VERTICAL PAINT COVERAGE — the axis that actually escaped the headless
        // suite. R5's mobile stack made the v4 player taller than
        // `.widget-wrap`'s 624px max-height, so ~125px of list rows painted
        // BELOW the coloured box at 320 (105px at 360) — white-on-black on the
        // host page. R5/R6's assertions were horizontal-only and passed it
        // through; it was caught by eye on the deployed build (15b949b). This
        // reports the painted background box against the measured content so a
        // recurrence is visible in the field, not just to whoever looks.
        const bg = el && el.querySelector ? el.querySelector('.widget-wrap') : null;
        const bgH = bg && bg.offsetHeight ? bg.offsetHeight : 0;
        // >0 means content is painting past the bottom of the coloured box.
        const paintGap = bgH > 0 && contentH > bgH ? contentH - bgH : 0;

        const art = el && el.querySelector ? el.querySelector(ARTWORK_SELECTOR) : null;
        const artH = art && art.offsetHeight ? art.offsetHeight : 0;
        // HORIZONTAL AXIS — the one nothing reported. Measured across
        // v1/v2/v3/v4 x 320/390/1280 with a real cover: v4's cover is crushed
        // from 172x184 to 10x184 between 1280 and 320, aspect destroyed, while
        // artwork_h holds a healthy-looking 184 the whole way down. Without
        // this the embed is byte-identical in telemetry to a correct one — the
        // same blindness the v4/v2/v3-mini selector gap had, on the other axis.
        const artW = art && art.offsetWidth ? art.offsetWidth : 0;
        // …and it is also the layout discriminator. A STACKED cover spans the
        // full content width (measured 1.00 on every phone case); a
        // side-by-side one never exceeded 0.47 (v3 desktop). 0.6 splits them
        // with margin on both sides.
        const artStacked = artW > 0 && contentW > 0 && artW >= contentW * 0.6;
        // multi-station embeds (stations.length > 1, supported via requestAll)
        // report only the first, so a defect on the second is misattributed.
        const station = (this.stations && this.stations[0] && this.stations[0].station) || '';
        const stationCount = (this.stations && this.stations.length) || 0;
        const mods = (this.queryParams && this.queryParams.mods) || '';
        pushTelemetryEvent('widget_layout', {
          schema: '1',
          version: String(this.version),
          requested_version: String(this.requestedVersion),
          // 🔴 normalizeStation, NOT String(). `withEmbedContext` guards on
          // hasOwnProperty — a caller that supplies its own `station` key wins over the
          // module-level stamp, on purpose — and `widget_layout` is the ONE of five
          // pushTelemetryEvent sites that supplies one. So this line, and only this line,
          // bypassed the normalization: MEASURED on postv4 over 13h, 16,138 widget_layout
          // events, 49.7% under an uppercase label, 30 stations split across both cases,
          // while `user_attr_station` on the very same event was already lowercase.
          station: normalizeStation(station),
          mods: String(mods),
          station_count: String(stationCount),
          theme: this.isDarkTheme ? 'dark' : 'light',
          screen_w: String(screenW),
          screen_h: String(screenH),
          // 'true' => screen_w/screen_h above are NOT device dimensions (D-53).
          screen_is_frame_box: screenIsFrameBox,
          viewport_w: String(innerW),
          viewport_h: String(innerH),
          visual_w: vv && vv.width ? String(Math.round(vv.width)) : '',
          visual_h: vv && vv.height ? String(Math.round(vv.height)) : '',
          dpr: w.devicePixelRatio ? String(w.devicePixelRatio) : '',
          orientation,
          content_w: String(contentW),
          content_h: String(contentH),
          artwork_h: String(artH),
          artwork_w: String(artW),
          // Distinguishes "no artwork rendered" from "the selector missed it".
          // Without it the next selector drift is silent, exactly as the
          // v4/v2/v3-mini gap was.
          artwork_found: art ? 'true' : 'false',
          // Whether artwork_h is a SETTLED reading or a give-up. Both prior bugs
          // in this beacon were "the artwork axis was sampled at the wrong
          // moment", and nothing in the payload let a querier tell a good read
          // from a bad one — so a wrong value looked exactly like a right one.
          //
          // 'false' means the ~3s ceiling expired before any cover reported
          // loaded. Known live case: v2 on a slow connection. v2's box is laid
          // out at the width/height ATTRIBUTE aspect until the pixels arrive and
          // then reflows to the image's INTRINSIC aspect, and that final size
          // cannot be reserved in CSS the way v4's square cap can — measured with
          // a 2s cover, artwork_h=204 against a settled 393. So filter on
          // `artwork_settled="true"` before trusting artwork_h or art_dominates.
          artwork_settled: this._artworkSignalled || !art ? 'true' : 'false',
          bg_h: String(bgH),
          paint_gap: String(paintGap),
          // the R5-class regression, as a single queryable flag
          paint_uncovered: paintGap > 0 ? 'true' : 'false',
          // content wider than the iframe box = the horizontal overflow the
          // resize handshake can post past the host column (v1/v4 scrollWidth).
          overflow: contentW > innerW ? 'true' : 'false',
          // VERTICAL clipping — content taller than the box it is shown in, so
          // the tail is unreachable under the scrolling="no" most embeds carry.
          // This is #399's v4 symptom itself: measured on the deployed build at
          // 390px, iframe 579 against content 800, 221px of episode list simply
          // not reachable. Derivable from content_h/viewport_h, surfaced as a
          // flag because it is the single most useful query against this beacon.
          //
          // ⚠️ MEASURED FLAKY ON THE DEPLOYED BUILD — it can false-positive.
          // Two runs of the same probe against the same build read viewport_h
          // 260 then 590 for v4 (settled box 590) and 67 then 307 for v1
          // (settled 307), so the first run flagged clipped_v="true" on two
          // embeds that are not clipped. The emit already sits below
          // postToParent plus two rAFs, which suffices in the e2e harness
          // (same-process parent) but is NOT a guarantee against a real
          // embedder: applying the posted height is a cross-document round trip
          // whose timing moves with the network. Treat a lone clipped_v="true"
          // as a lead, not a finding — corroborate against the embed's real box
          // or look for the flag persisting across many sessions on one station.
          // art_dominates divides by the same innerH and inherits this.
          clipped_v: contentH > innerH ? 'true' : 'false',
          // #399's OTHER symptom: the cover eating the embed. The predicate was
          // `artH > screen.height / 2` and was UNREACHABLE — artwork is capped
          // around 240 and a phone screen is 640-932, so it read false on every
          // phone embed we ship. Measured (v1/v2/v3/v4 x 320/390/1280, real
          // cover routed) artH/screenH peaked at 0.45, including on v1 @ 390,
          // which is a full-bleed 358px cover on a 390px phone — the literal
          // #399 case. A flag that cannot fire on its own defect is worse than
          // no flag: it reads as evidence of health.
          //
          // The denominator with signal is the embed's own VISIBLE box. The
          // guard against the obvious false positive is the layout axis:
          // v3 @ 1280 has the highest height share measured anywhere (0.87) and
          // is entirely healthy — cover left, info right, nothing buried. Its
          // width share (0.47) is what distinguishes it from a stacked cover
          // (1.00 on every phone case). So: stacked AND over half the box.
          //
          // Caveat for the querier: innerH is the POST-handshake iframe height
          // (the emit sits below postToParent, pinned by the viewport_h e2e
          // test). If that ordering is ever reverted this reads against the
          // hardcoded 224 default and over-fires. Filter on
          // artwork_settled="true" as well — a pending cover measures small.
          art_dominates: artStacked && innerH > 0 && artH > innerH / 2 ? 'true' : 'false',
        });
      } catch (e) {
        // telemetry must never surface to the embed
      }
    },

    handleOnPressShowMore() {
      this.hashTagModalIsVisible = !this.hashTagModalIsVisible;
    },
  },
  watch: {
    podcasts: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.informResize();
        });
      },
    },
  },
};
</script>

<style lang="less">
body {
  margin: 0;
  padding: 0;
}
#widget-container {
  width: 100%;
  max-width: 700px;
}
.spacer {
  margin-bottom: 26px;
}
</style>
